module Meevo.Plugins.Register {

    export class RegisterGiftCardService {
        static $inject = [
            "$q",
            "meevo.components.common.DataService",
            "meevo.components.common.IdentityService",
            "meevo.AppSettings"
        ];

        constructor(
            protected $q: ng.IQService,
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected identityService: Meevo.Components.Common.IIdentityService,
            protected appSettings: Meevo.Components.Common.IAppSettings
        ) {
        }

        checkGCUsageRestriction(giftCard: any, canUseExpired: boolean = true): boolean {
            if (this.appSettings["APP_BUSINFOSETTINGS"].registerSettings.restrictGiftCardViewingRedemption) {
                if (giftCard.saleLocationId !== this.identityService.locationId) {
                    return false;
                }
            }

            if (!canUseExpired && giftCard.isExpired) {
                return false;
            }

            return true;
        }

        calculateGiftCardRefundOffset(saleLine, clientGiftCard) {
            //TODO: move this to the server

            const offset = {
                remainingAmount: 0,
                remainingRefundAmount: 0,
            };

            // this offset gets set to saleLine as basePrice and finalPrice
            // then when the saleLine is applied as refund, they get negated
            // basePrice reduces the client giftcard remainingAmount (value)
            // finalPrice reduces the client giftcard remainingRefundAmount

            // for gift card refund
            // - if remainingRefundAmount is reduced to 0, then remainingAmount is also reduced to 0 otherwise remainingAmount remains the same
            // for gift card reload refund
            // - the original gift card reload is refunded from the client giftcard

            if (saleLine.saleType === Model.SalesTypeEnum.GiftCard) {
                // compare the lower of the remainingAmount (value) or the gift card finalPrice (this is to account for gift card usages since original sale)
                // if either is higher than the remainingRefundAmount, then zero out the remainingAmount (value)
                //   otherwise leave remainingAmount (value) the same
                const refundAmount = Math.min(clientGiftCard.remainingAmount, saleLine.finalPrice);
                offset.remainingAmount = refundAmount >= clientGiftCard.remainingRefundAmount
                    ? clientGiftCard.remainingAmount /* zero out the remainingAmount */
                    : 0;
            }
            else {
                // compare the gift card reload finalPrice
                // if it is higher than the remainingRefundAmount, then zero out the remainingAmount,
                //   otherwise reduce it by remainingAmount (value) (capped to gift card reload basePrice)
                offset.remainingAmount = saleLine.finalPrice > clientGiftCard.remainingRefundAmount
                    ? clientGiftCard.remainingAmount
                    : Math.min(clientGiftCard.remainingAmount, saleLine.basePrice);
            }

            offset.remainingRefundAmount = Math.min(clientGiftCard.remainingRefundAmount, saleLine.finalPrice);

            return offset;
        }

        testCalculateGiftCardRefundOffset() {
            // run this from console anywhere in meevo:
            // angular.element('*[ng-app]').injector().get('meevo.plugins.register.RegisterGiftCardService').testCalculateGiftCardRefundOffset()

            const GC = Model.SalesTypeEnum.GiftCard;
            const GCR = Model.SalesTypeEnum.ReloadGiftCard;

            const generateTest = (
                saleType: Model.SalesTypeEnum,
                basePrice: number,
                finalPrice: number,
                otherBasePrice: number,
                otherFinalPrice: number,
                usage: number,
                expectedRemainingAmount: number,
                expectedRemainingRefundAmount: number,
                desc: string
            ) => {
                return {
                    desc: desc,
                    saleLine: { saleType: saleType, basePrice: basePrice, finalPrice: finalPrice },
                    saleLine2: { basePrice: otherBasePrice, finalPrice: otherFinalPrice },
                    usage: usage,
                    giftCard: {
                        remainingAmount: usage >= 0
                            ? Math.max(0, basePrice + otherBasePrice - usage)
                            : Math.max(0, basePrice + usage) + otherBasePrice,
                        remainingRefundAmount: usage >= 0
                            ? Math.max(0, finalPrice + otherFinalPrice - usage)
                            : Math.max(0, finalPrice + usage) + otherFinalPrice
                    },
                    offset: null,
                    expected: { remainingAmount: expectedRemainingAmount, remainingRefundAmount: expectedRemainingRefundAmount },
                }
            };

            const tests = [
                //           sale           reload/GC   usage   expected  desc
                generateTest(GC,   50, 50,    0,  0,      0,      0,  0,  'gift card - no reload - no usage' ),
                generateTest(GC,  100, 50,    0,  0,      0,      0,  0,  'gift card - no reload - no usage' ),
                generateTest(GC,   50,  0,    0,  0,      0,      0,  0,  'gift card - no reload - no usage' ),

                generateTest(GC,   50, 50,    0,  0,     10,      0,  0,  'gift card - no reload - partial usage ($10)' ),
                generateTest(GC,  100, 50,    0,  0,     10,      0,  0,  'gift card - no reload - partial usage ($10)' ),
                generateTest(GC,   50,  0,    0,  0,     10,      0,  0,  'gift card - no reload - partial usage ($10)' ),

                generateTest(GC,   50, 50,    0,  0,     50,      0,  0,  'gift card - no reload - full usage' ),
                generateTest(GC,  100, 50,    0,  0,    100,      0,  0,  'gift card - no reload - full usage' ),
                generateTest(GC,   50,  0,    0,  0,     50,      0,  0,  'gift card - no reload - full usage' ),

                generateTest(GC,   50, 50,   20, 20,      0,     70, 20,  'gift card - $20/$20 reload - no usage' ),
                generateTest(GC,  100, 50,   20, 20,      0,    120, 20,  'gift card - $20/$20 reload - no usage' ),
                generateTest(GC,   50,  0,   20, 20,      0,     70, 20,  'gift card - $20/$20 reload - no usage' ),

                generateTest(GC,   50, 50,   20, 20,    -10,     60, 10,  'gift card - $20/$20 reload - partial usage ($10) before reload' ),
                generateTest(GC,  100, 50,   20, 20,    -10,    110, 10,  'gift card - $20/$20 reload - partial usage ($10) before reload' ),
                generateTest(GC,   50,  0,   20, 20,    -10,     60, 20,  'gift card - $20/$20 reload - partial usage ($10) before reload' ),

                generateTest(GC,   50, 50,   20, 20,     10,     60, 10,  'gift card - $20/$20 reload - partial usage ($10) after reload' ),
                generateTest(GC,  100, 50,   20, 20,     10,    110, 10,  'gift card - $20/$20 reload - partial usage ($10) after reload' ),
                generateTest(GC,   50,  0,   20, 20,     10,     60, 10,  'gift card - $20/$20 reload - partial usage ($10) after reload' ),

                generateTest(GC,   50, 50,   20, 10,      0,     70, 10,  'gift card - $20/$10 reload - no usage' ),
                generateTest(GC,  100, 50,   20, 10,      0,    120, 10,  'gift card - $20/$10 reload - no usage' ),
                generateTest(GC,   50,  0,   20, 10,      0,     70, 10,  'gift card - $20/$10 reload - no usage' ),

                generateTest(GC,   50, 50,   20, 10,    -10,      0,  0,  'gift card - $20/$10 reload - partial usage ($10) before reload' ),
                generateTest(GC,  100, 50,   20, 10,    -10,      0,  0,  'gift card - $20/$10 reload - partial usage ($10) before reload' ),
                generateTest(GC,   50,  0,   20, 10,    -10,     60, 10,  'gift card - $20/$10 reload - partial usage ($10) before reload' ),

                generateTest(GC,   50, 50,   20, 10,     10,      0,  0,  'gift card - $20/$10 reload - partial usage ($10) after reload' ),
                generateTest(GC,  100, 50,   20, 10,     10,      0,  0,  'gift card - $20/$10 reload - partial usage ($10) after reload' ),
                generateTest(GC,   50,  0,   20, 10,     10,      0,  0,  'gift card - $20/$10 reload - partial usage ($10) after reload' ),

                generateTest(GC,   50, 50,   20,  0,      0,      0,  0,  'gift card - $20/$0 reload - no usage' ),
                generateTest(GC,  100, 50,   20,  0,      0,      0,  0,  'gift card - $20/$0 reload - no usage' ),
                generateTest(GC,   50,  0,   20,  0,      0,      0,  0,  'gift card - $20/$0 reload - no usage' ),

                generateTest(GC,   50, 50,   20,  0,     10,      0,  0,  'gift card - $20/$0 reload - partial usage ($10)' ),
                generateTest(GC,  100, 50,   20,  0,     10,      0,  0,  'gift card - $20/$0 reload - partial usage ($10)' ),
                generateTest(GC,   50,  0,   20,  0,     10,      0,  0,  'gift card - $20/$0 reload - partial usage ($10)' ),

                generateTest(GCR,  30, 30,   50, 50,      0,     50, 50,  'gift card reload - no usage - GC $30/$30' ),
                generateTest(GCR,  30, 30,  100, 50,      0,    100, 50,  'gift card reload - no usage - GC $30/$30' ),
                generateTest(GCR,  30, 30,   50,  0,      0,     50,  0,  'gift card reload - no usage - GC $30/$30' ),

                generateTest(GCR,  30, 15,   50, 50,      0,     50, 50,  'gift card reload - no usage - GC $30/$15' ),
                generateTest(GCR,  30, 15,  100, 50,      0,    100, 50,  'gift card reload - no usage - GC $30/$15' ),
                generateTest(GCR,  30, 15,   50,  0,      0,     50,  0,  'gift card reload - no usage - GC $30/$15' ),

                generateTest(GCR,  30,  0,   50, 50,      0,     50, 50,  'gift card reload - no usage - GC $30/$0' ),
                generateTest(GCR,  30,  0,  100, 50,      0,    100, 50,  'gift card reload - no usage - GC $30/$0' ),
                generateTest(GCR,  30,  0,   50,  0,      0,     50,  0,  'gift card reload - no usage - GC $30/$0' ),

                generateTest(GCR,  30, 30,   50, 50,     10,     40, 40,  'gift card reload - partial usage ($10) after reload - GC $30/$30' ),
                generateTest(GCR,  30, 30,  100, 50,     10,     90, 40,  'gift card reload - partial usage ($10) after reload - GC $30/$30' ),
                generateTest(GCR,  30, 30,   50,  0,     10,      0,  0,  'gift card reload - partial usage ($10) after reload - GC $30/$30' ),

                generateTest(GCR,  30, 15,   50, 50,     10,     40, 40,  'gift card reload - partial usage ($10) after reload - GC $30/$15' ),
                generateTest(GCR,  30, 15,  100, 50,     10,     90, 40,  'gift card reload - partial usage ($10) after reload - GC $30/$15' ),
                generateTest(GCR,  30, 15,   50,  0,     10,      0,  0,  'gift card reload - partial usage ($10) after reload - GC $30/$15' ),

                generateTest(GCR,  30,  0,   50, 50,     10,     40, 40,  'gift card reload - partial usage ($10) after reload - GC $30/$0' ),
                generateTest(GCR,  30,  0,  100, 50,     10,     90, 40,  'gift card reload - partial usage ($10) after reload - GC $30/$0' ),
                generateTest(GCR,  30,  0,   50,  0,     10,     40,  0,  'gift card reload - partial usage ($10) after reload - GC $30/$0' ),

                generateTest(GCR,  30, 30,   50, 50,     60,      0,  0,  'gift card reload - partial usage ($60) after reload - GC $30/$30' ),
                generateTest(GCR,  30, 30,  100, 50,     60,      0,  0,  'gift card reload - partial usage ($60) after reload - GC $30/$30' ),
                generateTest(GCR,  30, 30,   50,  0,     60,      0,  0,  'gift card reload - partial usage ($60) after reload - GC $30/$30' ),

                generateTest(GCR,  30, 15,   50, 50,     60,      0,  0,  'gift card reload - partial usage ($60) after reload - GC $30/$15' ),
                generateTest(GCR,  30, 15,  100, 50,     60,      0,  0,  'gift card reload - partial usage ($60) after reload - GC $30/$15' ),
                generateTest(GCR,  30, 15,   50,  0,     60,      0,  0,  'gift card reload - partial usage ($60) after reload - GC $30/$15' ),

                generateTest(GCR,  30,  0,   50, 50,     60,      0,  0,  'gift card reload - partial usage ($60) after reload - GC $30/$0' ),
                generateTest(GCR,  30,  0,  100, 50,     60,     40,  0,  'gift card reload - partial usage ($60) after reload - GC $30/$0' ),
                generateTest(GCR,  30,  0,   50,  0,     60,      0,  0,  'gift card reload - partial usage ($60) after reload - GC $30/$0' ),

                generateTest(GCR,  30, 30,   50, 50,     80,      0,  0,  'gift card reload - full usage ($60) - GC $30/$30' ),
                generateTest(GCR,  30, 30,  100, 50,    130,      0,  0,  'gift card reload - full usage ($60) - GC $30/$30' ),
                generateTest(GCR,  30, 30,   50,  0,     80,      0,  0,  'gift card reload - full usage ($60) - GC $30/$30' ),
            ];

            const testResults = [];

            const isSuccess = (test) =>
                (test.giftCard.remainingAmount - test.offset.remainingAmount) === test.expected.remainingAmount &&
                (test.giftCard.remainingRefundAmount - test.offset.remainingRefundAmount) === test.expected.remainingRefundAmount;

            const executeTest = (test) => {
                test.offset = this.calculateGiftCardRefundOffset(test.saleLine, test.giftCard);
                testResults.push({
                    desc: test.desc,
                    success: isSuccess(test) ? '✅' : '❌',
                    ...test.saleLine,
                    usage: test.usage,
                    ...test.giftCard,
                    offsetRemainingAmount: -test.offset.remainingAmount,
                    offsetRemainingRefundAmount: -test.offset.remainingRefundAmount,
                    finalRemainingAmount: test.giftCard.remainingAmount - test.offset.remainingAmount,
                    finalRemainingRefundAmount: test.giftCard.remainingRefundAmount - test.offset.remainingRefundAmount,
                    expectedRemainingAmount: test.expected.remainingAmount,
                    expectedRemainingRefundAmount: test.expected.remainingRefundAmount,
                });
            }

            tests.forEach(test => executeTest(test));

            console.table(testResults);
        }
    }
}
angular.module("meevo.plugins.register")
    .service("meevo.plugins.register.RegisterGiftCardService", Meevo.Plugins.Register.RegisterGiftCardService);
